<template>
  <div
    ref="container"
    class="product-gallery critical-component-clear"
  >
    <div
      v-if="product"
      class="product-gallery__carousel-container"
      :js-overlap-element="isMobile"
    >
      <!-- Display the product gallery carousel on mobile -->
      <div
        v-if="isMobile"
        ref="carousel"
        class="product-gallery__carousel splide"
        js-overlap-element="hide"
        :js-carousel="galleryNamespace"
      >
        <div class="product-gallery__track splide__track">
          <ul class="splide__list">
            <template
              v-for="(image, index) of productImages"
              :key="index"
            >
              <li
                v-if="image?.media_type === 'video' || image?.mediaContentType === 'VIDEO'"
                ref="imageIndex"
                class="
                  product-gallery__slide
                  product-gallery__slide--video
                  splide__slide
                "
              >
                <span
                  class="visually-hidden"
                  v-text="image.alt || $string('accessibility.image_placeholder')"
                />
                <video-player
                  :src="getMp4VideoSrc(image)"
                  :poster="image.preview_image?.src"
                  autoplay
                  :controls="false"
                />
              </li>
              <li
                v-else
                ref="imageIndex"
                class="product-gallery__slide splide__slide"
                :style="flatImageBackground(image.alt)"
              >
                <responsive-image
                  :image="image"
                  min-max="2100-4800"
                  ratio="image"
                  loading="eager"
                />
              </li>
            </template>
          </ul>
        </div>

        <div class="product-gallery__mobile-details">
          <ul class="splide__pagination" />
        </div>
      </div>

      <!-- Desktop product gallery -->
      <div
        v-else-if="productImages"
        class="product-gallery__grid grid"
      >
        <template
          v-for="(image, index) of productImages"
          :key="index"
        >
          <!-- If the media is an image, display the image -->
          <button
            v-if="image?.media_type !== 'video' && image?.mediaContentType !== 'VIDEO'"
            ref="imageIndex"
            class="col l12 product-gallery__grid-image"
            :class="{
              ['product-gallery__grid-image--expanded']: galleryExpanded
            }"
            :style="flatImageBackground(image.alt)"
            :data-image-index="index"
            type="button"
            @click="handleImageClick(index)"
          >
            <span
              class="visually-hidden"
              v-text="image.alt || $string('accessibility.image_placeholder')"
            />

            <responsive-image
              block-class="product-gallery"
              :image="image"
              min-max="2100-4800"
              ratio="image"
            />
          </button>

          <!-- If the media is a video, display the video player component. -->
          <button
            v-else-if="image?.media_type === 'video' || image?.mediaContentType === 'VIDEO'"
            ref="imageIndex"
            class="col l12 product-gallery__grid-image"
            :data-image-index="index"
            type="button"
            @click="handleImageClick(index)"
          >
            <span
              class="visually-hidden"
              v-text="image.alt || $string('accessibility.image_placeholder')"
            />
            <video-player
              autoplay
              loop
              muted
              playsinline
              :controls="false"
              :src="getVideoSource(image)"
              :poster="getPreviewImage(image)"
            />
          </button>
        </template>
      </div>
    </div>

    <!-- Display thumbnails on desktop only. Hidden with CSS. -->
    <product-thumbnails
      class="product-gallery__thumbnails"
      :gallery-expanded="galleryExpanded"
      :images="productImages"
      :image-index="viewedIndex"
      @thumbnail-clicked="handleThumbnailClicked"
    />
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Base component for the Product Gallery carousel. Will house the Zoom viewer
 * and 3D Explorer.
 *
 * @param {String} namespace - Unique namespace for the Gallery.
 * @param {Object|Boolean} product - CANVAS formatted product object.
 * @param {Object|Boolean} variant - CANVAS formatted variant object.
 *
 */
import { nextTick } from 'vue'
import { mapState } from 'vuex'

import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'

import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'

import Panzoom from '@panzoom/panzoom'

import Carousel from '~/api/carousel'

import ResponsiveImage from '~global/images/responsive-image'
import ProductThumbnails from '~async/product-thumbnails/product-thumbnails'
import VideoPlayer from '~async/video-player/video-player'

// eslint-disable-next-line no-undef
gsap.registerPlugin(ScrollToPlugin)

export default {
  name: 'ProductGallery',

  components: {
    ResponsiveImage,
    ProductThumbnails,
    VideoPlayer,
  },

  props: {
    namespace: {
      type: String,
      default: 'ProductGallery',
    },

    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },

    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'gallery-state-changed': (payload) => typeof payload === 'boolean',
    'zoom-updated': (payload) => typeof payload === 'boolean',
    'carousel-image-changed': (payload) => typeof payload === 'boolean',
    'last-slide-reached': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      carousel: false,
      carouselConfig: {
        arrows: false,
        autoplay: false,
        drag: 'free',
        flickPower: 300,
        flickMaxPages: 1,
        pagination: false,
        perPage: 1,
        releaseWheel: true,
        rewind: false,
        speed: 1000,
        snap: true,
        type: 'slide',
        updateOnMove: true,
        wheel: false,
        wheelMinThreshold: '15',
        wheelSleep: '750',
      },
      carouselIndex: 0,
      clickedImage: false,
      currentSlide: '',
      focussedElement: 0,
      galleryExpanded: false,
      imageOffset: 0,
      panzoom: false,
      scrollState: 'idle',
      zoomState: false,
      selectedImage: 0,
      touchPosition: 0,
      touchDirection: '',
      viewedIndex: 0,
      viewportHeight: window.visualViewport.height * 0.8,
    }
  },

  computed: {

    /**
     * Adds the additional image to the image array if product.images is odd.
     * @returns {Boolean, Array}
     */
    productImages() {
      if (!this.product) {
        return []
      }

      const mediaObject = [...this.product.images]

      if (this.product?.video?.length) {
        this.product.video.forEach((item) => {
          mediaObject.splice(item.position - 1, 0, item)
        })
      }

      return mediaObject
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Calculate a unique namespace for the Gallery.
     * @returns {String}
     */
    galleryNamespace() {
      return `${this.namespace}-Gallery`
    },

    /**
     * Calculate if images exist and if to show the gallery.
     * @returns {Boolean}
     */
    showGallery() {
      return Boolean(this.isMobile || this.$parent.$options.name === 'QuickView')
    },

    /**
     * Returns an index + 1 format for the present carousel index.
     * @returns {Number}
     */
    formattedCarouselIndex() {
      return this.carouselIndex + 1
    },
  },

  watch: {

    /**
     * When the galleryExpanded state changes play or reverse the gsap
     * animation.
     */
    galleryExpanded(value) {
      const self = this

      this.toggleHeaderStatusClass()
      this.$emit('gallery-state-changed', value)
      if (!this.zoomState) {
        this.zoomState = true
      }

      if (!value) {
        document.body.classList.remove('gallery-expanded')
        clearAllBodyScrollLocks()

        return
      }

      document.body.classList.add('gallery-expanded')
      disableBodyScroll(document.querySelector('.product-gallery'))

      setTimeout(() => {
        self.clickedImage.scrollIntoView(true)
      })
    },

    /**
     * When the carousel index changes, update the viewed index.
     */
    panzoom(value) {
      this.$emit('zoom-updated', Boolean(value))
      if (!this.carousel) {
        return
      }

      if (!value) {
        this.carousel.Components.Drag.disable(false)
        return
      }
      this.carousel.Components.Drag.disable(true)
    },

    /**
     * Watch the product object.
     */
    product: {
      handler() {
        this.carouselIndex = 0
        if (!this.product) {
          return
        }

        nextTick().then(() => {
          this.setImageIntersectionObservers()

          /**
           * Create a carousel only on mobile.
           */
          if (!this.isMobile) {
            return
          }

          if (this.carousel) {
            this.carousel.destroy()
          }

          this.createCarousel()
        })
      },
      immediate: true,
    },

    /**
     * Watch the active variant.
     * - Indicates that the PDP form has updated.
     */
    variant: {
      handler() {
        if (
          !this.variant ||
          !this.$refs.carousel ||
          !this.product?.images.length) {
          return
        }

        /**
         * Find the relevant image index by the active variant id.
         */
        const variantIndex = this.product.images.findIndex((image) => {
          return Number(image.id) === Number(this.variant.image)
        })

        if (variantIndex < 0 || variantIndex > this.product.images.length) {
          return
        }

        if (typeof this.carousel === 'boolean') {
          return
        }

        this.carousel.go(variantIndex)
      },
      deep: true,
    },

    /**
     * Scroll to image when thumbnail is clicked.
     */
    selectedImage() {
      if (this.scrollState !== 'scrolling') {
        return
      }

      this.scrollToImage()
    },

    /**
     * Emits event when carousel index is updated
     */
    formattedCarouselIndex() {
      this.$emit('carousel-image-changed', this.formattedCarouselIndex)
    },
  },

  mounted() {
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.removePlaceholders()
      }, this.$timing('normal'))
    })
  },

  methods: {

    /**
     * Returns the mp4 src url for the given video.
     * @param {Object} video - Video object of 2 possible formats.
     * @returns {String}
     */
    getMp4VideoSrc(video) {
      const source = video?.sources?.find(src => src.format === 'mp4')
      return source?.url
    },

    /**
     * Returns the preview image from the 2 video formats.
     * @param {Object} video - Video object of 2 possible formats.
     * @returns {String}
     */
    getPreviewImage(video) {
      return video.preview_image?.src
        ? video.preview_image.src
        : video.previewImage.url || null
    },

    /**
     * Returns the video src from the 2 video formats.
     * @param {Object} video - Video object of 2 possible formats.
     * @returns {String}
     */
    getVideoSource(video) {
      const source = video.sources.find(src => src.format === 'mp4')
      return source?.url
    },

    /**
     * Removes the liquid placeholders AFTER the vue media has loaded
     * and fully rendered.
     */
    removePlaceholders() {
      const placeholder = document.querySelector('.main-product__parent > .liquid-removed')
      if (!placeholder) {
        return
      }
      placeholder.remove()
    },

    /**
     * When the gallery expands, add a class to the header to prevent the
     * overflow logic affecting it and resetting its transform position.
     */
    toggleHeaderStatusClass() {
      const header = document.querySelector('.site-header')

      if (!header) {
        return
      }

      header.classList.toggle('site-header--gallery-expanded')
    },

    /**
     * Tidies up properties left by GSAP during animation that interfere with
     * transitions and animations elsewhere.
     */
    resetElementProperties() {
      const contentColumn = document.querySelector('.main-product__content-column')
      if (!contentColumn) {
        return
      }

      contentColumn.style.removeProperty('transform')
    },

    /**
     * Handles the thumbnail click events.
     * @param {Number} index - Index of the thumbnail clicked.
     */
    handleThumbnailClicked(index) {
      this.selectedImage = index
      this.scrollState = 'scrolling'
    },

    /**
     * Scroll the currently selected image into view.
     */
    scrollToImage() {
      const element = this.$refs.imageIndex[this.selectedImage]

      if (!element || this.scrollState !== 'scrolling') {
        return
      }

      gsap.to(this.$refs.container, {
        scrollTo: {
          y: element,
        },
      })

      this.scrollState = 'idle'
    },

    /**
     * Handle carousel creation on mobile.
     */
    createCarousel() {
      this.carousel = Carousel(`[js-carousel="${this.galleryNamespace}"]`, {
        ...this.carouselConfig,
        classes: {
          pagination: 'splide__pagination product-gallery__mobile-pagination',
          page: 'splide__pagination__page product-gallery__pagination-square',
        },
      })

      /**
       * When a slide updates, save the index to data.
       */
      const events = ['active', 'move']

      events.forEach((event) => {
        this.carousel.on(event, () => {
          this.carouselIndex = this.carousel.index
        })
      })

      /**
       * Component initiates.
       */
      this.carousel.mount()
    },

    /**
     * Gets the currently viewed image to set as the current index.
     */
    setImageIntersectionObservers() {
      if (this.isMobile) {
        return
      }

      const self = this
      const images = [...document.querySelectorAll('.product-gallery__grid-image')]

      const observerOptions = {
        rootMargin: '-50% 0% -50% 0%',
        threshold: 0,
      }

      const observer = new IntersectionObserver(observerCallback, observerOptions)

      function observerCallback(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (self.scrollState === 'scrolling') {
              return
            }
            self.focussedElement = entry.target
            self.viewedIndex = Number(entry.target.dataset.imageIndex)
          }
        })
      }

      images.forEach(image => {
        observer.observe(image)
      })
    },

    /**
     * Handles click events on the gallery images.
     * @param {Number} index - Index in the for loop of the clicked image.
     */
    handleImageClick(index) {
      this.galleryExpanded = !this.galleryExpanded
      this.selectedImage = index

      const element = this.$refs.imageIndex[index]
      this.clickedImage = element

    },

    /**
     * Handles click events on the carousel images.
     * @param {Number} index - Index in the for loop of the clicked image.
     */
    handleCarouselImageClick(index) {
      this.galleryExpanded = !this.galleryExpanded

      if (!this.panzoom) {
        const element = this.$refs.imageIndex[index]
        const image = element.querySelector('.images')
        const panzoom = Panzoom(image, {
          maxScale: 5,
          minScale: 1,
          contain: 'outside',
        })
        panzoom.pan(0, 0)
        panzoom.zoom(1)
        this.panzoom = panzoom
        this.carousel.options = { height: `${window.visualViewport.height}px` }
        return
      }

      this.carousel.options = { height: `${this.viewportHeight}px` }

      this.resetGalleryZoom()
    },

    /**
     * Resets the zoom state on the gallery carousel.
     */
    resetGalleryZoom() {
      if (!this.panzoom) {
        return
      }

      this.panzoom.reset({ animate: false })
      this.panzoom.destroy()
      this.panzoom = null
    },

    /**
     * Returs globally set background colour for product flat images.
     * @param {String} alt - Image alt text.
     * @returns {String}
     */
    flatImageBackground(alt) {
      return alt?.includes('flat_image') ? `background-color: ${cnvs.settings.flatImageBackground}` : ''
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery';
</style>
